/* ============================================================
   GWONG — theme.js
   Global utilities, sticky header, announcement bar, mobile drawer,
   accordions/tabs, lazy interactions.
   ============================================================ */
(function () {
  'use strict';

  /* Legacy #results anchor redirected — was causing page jump to before/after on load */
  if (window.location.hash === '#results') {
    history.replaceState(null, '', `${window.location.pathname}${window.location.search}#before-after`);
  }

  const $ = (s, ctx = document) => ctx.querySelector(s);
  const $$ = (s, ctx = document) => Array.from(ctx.querySelectorAll(s));

  window.Gwong = window.Gwong || {};
  window.Gwong.utils = {
    on(el, evt, fn, opts) { if (el) el.addEventListener(evt, fn, opts); },
    off(el, evt, fn) { if (el) el.removeEventListener(evt, fn); },
    debounce(fn, wait = 100) {
      let t;
      return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
    },
    setCookie(name, value, days) {
      const d = new Date();
      d.setTime(d.getTime() + days * 864e5);
      document.cookie = `${name}=${value};expires=${d.toUTCString()};path=/;SameSite=Lax`;
    },
    getCookie(name) {
      const m = document.cookie.match('(^|;)\\s*' + name + '=([^;]+)');
      return m ? m[2] : null;
    },
    money(cents, currency = 'CAD') {
      try {
        const formatted = new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
        return /\bCAD\b/i.test(formatted) ? formatted : `${formatted} CAD`;
      } catch (e) {
        return '$' + (cents / 100).toFixed(2) + ' CAD';
      }
    },
    focusTrap(container) {
      const focusable = container.querySelectorAll(
        'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return () => {};
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      function handle(e) {
        if (e.key !== 'Tab') return;
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
      container.addEventListener('keydown', handle);
      return () => container.removeEventListener('keydown', handle);
    },
    /** Hide prev at scroll start, next at scroll end (horizontal carousels). */
    updateScrollNav(viewport, prevBtn, nextBtn, tolerance = 2) {
      if (!viewport) return;
      const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const atStart = viewport.scrollLeft <= tolerance;
      const atEnd = viewport.scrollLeft >= max - tolerance;
      const noScroll = max <= tolerance;
      if (prevBtn) prevBtn.hidden = noScroll || atStart;
      if (nextBtn) nextBtn.hidden = noScroll || atEnd;
    },
  };

  /** Klaviyo opt-in with Shopify contact fallback (footer + popups). */
  async function submitEmail(email, source) {
    const conf = window.gwongConfig || {};
    if (conf.klaviyoCompanyId && conf.klaviyoListId) {
      try {
        await fetch(
          'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(conf.klaviyoCompanyId),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision: '2024-02-15' },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  profile: { data: { type: 'profile', attributes: { email } } },
                  custom_source: source || 'gwong-theme',
                },
                relationships: { list: { data: { type: 'list', id: conf.klaviyoListId } } },
              },
            }),
          }
        );
        return true;
      } catch (e) {
        console.warn('Klaviyo failed, falling back', e);
      }
    }
    const fd = new FormData();
    fd.append('form_type', 'customer');
    fd.append('utf8', '\u2713');
    fd.append('contact[email]', email);
    fd.append('contact[tags]', 'newsletter,' + (source || 'popup'));
    await fetch('/contact', { method: 'POST', body: fd });
    return true;
  }
  window.Gwong.submitEmail = submitEmail;

  // ---------- Header group chrome — announcement fixed on top, nav hides on scroll down ----------
  const headerEl = $('[data-header]') || $('.cb-header-wrap');
  const headerGroup = headerEl?.closest('.shopify-section-group-header-group');
  const headerSection = headerEl?.closest('.shopify-section');
  const announcementEl = headerGroup?.querySelector('[data-announcement]') || $('.cb-announcement');

  const getHeaderOffset = () => {
    const annH = announcementEl?.offsetHeight || 0;
    if (!headerEl || headerEl.classList.contains('is-header-hidden')) {
      return { headerH: 0, annH, total: annH };
    }
    const headerH = headerEl.offsetHeight;
    return { headerH, annH, total: annH + headerH };
  };

  window.Gwong.getSiteChromeHeight = getHeaderOffset;

  if (headerEl && headerGroup) {
    let lastScrollY = window.scrollY;
    let scrollTicking = false;
    const HEADER_SCROLL_DELTA = 10;

    let chromeSpacer = document.getElementById('site-chrome-spacer');
    if (!chromeSpacer) {
      chromeSpacer = document.createElement('div');
      chromeSpacer.id = 'site-chrome-spacer';
      chromeSpacer.setAttribute('aria-hidden', 'true');
    }
    if (headerSection) headerSection.after(chromeSpacer);
    else headerGroup.appendChild(chromeSpacer);

    const measureHeader = () => {
      const annH = announcementEl?.offsetHeight || 0;
      const headerH = headerEl.offsetHeight;
      const totalH = annH + headerH;
      document.documentElement.style.setProperty('--announcement-height', `${annH}px`);
      document.documentElement.style.setProperty('--cb-header-height', `${headerH}px`);
      document.documentElement.style.setProperty('--site-chrome-height', `${totalH}px`);
      chromeSpacer.style.height = `${totalH}px`;
      syncHeaderOffset();
    };

    const syncHeaderOffset = () => {
      const { total } = getHeaderOffset();
      document.documentElement.style.setProperty('--cb-header-offset', `${total}px`);
      document.dispatchEvent(new CustomEvent('gwong:chrome'));
    };

    const syncHeaderScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop;
      const hidden = headerEl.classList.contains('is-header-hidden');

      headerEl.classList.toggle('is-header-scrolled', y > 8 && !hidden);

      if (y < 16) {
        headerEl.classList.remove('is-header-hidden');
      } else if (y > lastScrollY + HEADER_SCROLL_DELTA) {
        headerEl.classList.add('is-header-hidden');
      } else if (y < lastScrollY - HEADER_SCROLL_DELTA) {
        headerEl.classList.remove('is-header-hidden');
      }

      if (hidden !== headerEl.classList.contains('is-header-hidden')) {
        syncHeaderOffset();
      }

      lastScrollY = y;
      scrollTicking = false;
    };

    measureHeader();
    syncHeaderOffset();
    window.addEventListener('scroll', () => {
      if (!scrollTicking) {
        scrollTicking = true;
        requestAnimationFrame(syncHeaderScroll);
      }
    }, { passive: true });
    window.addEventListener('resize', window.Gwong.utils.debounce(measureHeader, 150));
    if (document.fonts?.ready) document.fonts.ready.then(measureHeader);
    syncHeaderScroll();
  }

  // ---------- Announcement bar carousel ----------
  $$('.announcement').forEach((bar) => {
    const slides = $$('.announcement__slide', bar);
    if (slides.length < 2) {
      if (slides[0]) slides[0].classList.add('is-active');
      return;
    }
    let idx = 0;
    slides[0].classList.add('is-active');
    setInterval(() => {
      slides[idx].classList.remove('is-active');
      idx = (idx + 1) % slides.length;
      slides[idx].classList.add('is-active');
    }, 4000);
  });

  // ---------- Mobile drawer (portal to body so position:fixed works) ----------
  const drawer = $('.mobile-drawer');
  const drawerOverlay = $('.mobile-drawer-overlay');
  const openBtns = $$('[data-open-mobile-drawer]');

  if (drawer && drawer.parentElement !== document.body) {
    if (drawerOverlay) document.body.appendChild(drawerOverlay);
    document.body.appendChild(drawer);
  }

  let releaseTrap = () => {};
  function setDrawerOpen(isOpen) {
    openBtns.forEach((btn) => btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false'));
  }
  function openDrawer(e) {
    if (e) e.preventDefault();
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    if (drawerOverlay) drawerOverlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
    setDrawerOpen(true);
    releaseTrap = window.Gwong.utils.focusTrap(drawer);
    const f = drawer.querySelector('a, button');
    if (f) f.focus();
  }
  function closeDrawer() {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    if (drawerOverlay) drawerOverlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');
    setDrawerOpen(false);
    releaseTrap();
  }
  document.addEventListener('click', (e) => {
    const openTrigger = e.target.closest('[data-open-mobile-drawer]');
    if (openTrigger) {
      e.preventDefault();
      e.stopPropagation();
      openDrawer(e);
      return;
    }
    if (e.target.closest('[data-close-mobile-drawer]')) {
      e.preventDefault();
      closeDrawer();
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer && drawer.getAttribute('aria-hidden') === 'false') closeDrawer();
  });
  window.addEventListener('resize', window.Gwong.utils.debounce(() => {
    if (window.innerWidth >= 1024 && drawer && drawer.getAttribute('aria-hidden') === 'false') closeDrawer();
  }, 200));

  // ---------- Desktop nav dropdowns (CSS hover + touch/keyboard toggle) ----------
  const closeAllNavDropdowns = () => {
    $$('.cb-nav-item--dropdown.is-open').forEach((el) => {
      el.classList.remove('is-open');
      $('.cb-nav-link', el)?.setAttribute('aria-expanded', 'false');
    });
  };

  $$('.cb-nav-item--dropdown').forEach((item) => {
    const trigger = $('.cb-nav-link', item);
    const panel = $('.cb-nav-dropdown', item);
    if (!trigger || !panel) return;

    const open = () => {
      closeAllNavDropdowns();
      item.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
    };

    const close = () => {
      item.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
    };

    item.addEventListener('mouseenter', () => {
      if (window.innerWidth >= 1024) open();
    });

    item.addEventListener('mouseleave', (e) => {
      if (window.innerWidth < 1024) return;
      const to = e.relatedTarget;
      if (to && item.contains(to)) return;
      close();
    });

    trigger.addEventListener('click', (e) => {
      if (window.innerWidth < 1024) return;
      if (!item.classList.contains('is-open')) {
        e.preventDefault();
        open();
      }
    });

    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        if (window.innerWidth >= 1024 && !item.classList.contains('is-open')) {
          e.preventDefault();
          open();
        }
      }
      if (e.key === 'Escape') close();
    });
  });

  document.addEventListener('click', (e) => {
    if (!e.target.closest('.cb-nav-item--dropdown')) closeAllNavDropdowns();
  });

  // ---------- Tabs (Red/Blue/NIR on product page etc.) ----------
  $$('[data-tabs]').forEach((root) => {
    const btns = $$('[role="tab"]', root);
    const panels = $$('[role="tabpanel"]', root);
    btns.forEach((btn, i) => {
      btn.addEventListener('click', () => {
        btns.forEach((b) => b.setAttribute('aria-selected', 'false'));
        panels.forEach((p) => p.setAttribute('aria-hidden', 'true'));
        btn.setAttribute('aria-selected', 'true');
        if (panels[i]) panels[i].setAttribute('aria-hidden', 'false');
      });
    });
    if (btns[0]) btns[0].click();
  });

  // ---------- Filter chips (reviews) ----------
  $$('[data-filter-chips]').forEach((root) => {
    const chips = $$('.filter-chip, .cb-pdp-reviews__filter', root);
    const list = document.querySelector('[data-reviews-list]');
    chips.forEach((c) => {
      c.addEventListener('click', () => {
        chips.forEach((x) => {
          x.setAttribute('aria-pressed', 'false');
          x.classList.remove('is-active');
        });
        c.setAttribute('aria-pressed', 'true');
        c.classList.add('is-active');
        const value = c.dataset.value;
        if (list) {
          $$('[data-review-stars]', list).forEach((item) => {
            const stars = parseInt(item.dataset.reviewStars, 10) || 5;
            let show = true;
            if (value === '5') show = stars === 5;
            else if (value === '4') show = stars === 4;
            else if (value === 'helpful') show = stars >= 4;
            else show = true;
            item.hidden = !show;
          });
        }
        root.dispatchEvent(new CustomEvent('gwong:reviews:filter', { detail: { value } }));
      });
    });
  });

  // ---------- PDP reviews load more ----------
  const reviewsList = document.querySelector('[data-reviews-list]');
  const loadMoreBtn = document.querySelector('[data-reviews-load-more]');
  if (reviewsList && loadMoreBtn) {
    const items = $$('[data-review-stars]', reviewsList);
    const initial = 4;
    if (items.length > initial) {
      items.forEach((el, i) => { if (i >= initial) el.hidden = true; });
      loadMoreBtn.hidden = false;
      loadMoreBtn.addEventListener('click', () => {
        items.forEach((el) => { el.hidden = false; });
        loadMoreBtn.hidden = true;
      });
    }
  }

  // ---------- PDP science tabs ----------
  $$('[data-science-tabs]').forEach((root) => {
    const tabs = $$('[data-science-tab]', root);
    const panels = $$('[data-science-panel]', root);
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const idx = parseInt(tab.dataset.scienceTab, 10);
        tabs.forEach((t, i) => {
          const on = i === idx;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        panels.forEach((p, i) => {
          const on = i === idx;
          p.classList.toggle('is-active', on);
          p.hidden = !on;
        });
      });
    });
  });

  // ---------- Before/After slider (loaded lazily) ----------
  const beforeAfter = $$('.before-after');
  if (beforeAfter.length) {
    const script = document.createElement('script');
    script.src = document.currentScript ? document.currentScript.src.replace('theme.js', 'compare-slider.js') : '/assets/compare-slider.js';
    script.defer = true;
    document.head.appendChild(script);
  }

  // ---------- Quantity steppers ----------
  document.addEventListener('click', (e) => {
    const inc = e.target.closest('[data-qty-inc]');
    const dec = e.target.closest('[data-qty-dec]');
    if (!inc && !dec) return;
    const wrap = (inc || dec).closest('.qty, .cart-item__qty');
    if (!wrap) return;
    const input = wrap.querySelector('input[type="number"], input[name="quantity"]');
    if (!input) return;
    let v = parseInt(input.value, 10) || 1;
    if (inc) v += 1;
    if (dec) v = Math.max(1, v - 1);
    input.value = v;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // ---------- Header cart count live update ----------
  document.addEventListener('gwong:cart:updated', (e) => {
    const count = e.detail && typeof e.detail.item_count === 'number' ? e.detail.item_count : null;
    if (count === null) return;
    if (window.Gwong?.cart?.updateBadge) {
      window.Gwong.cart.updateBadge(count);
      return;
    }
    $$('[data-cart-count], .cb-cart__count, .header__cart-count').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count <= 0;
    });
  });

  // ---------- PDP hero columns (desktop flex + sticky gallery) ----------
  (function initPdpHeroLayout() {
    const mq = window.matchMedia('(min-width: 1024px)');

    document.querySelectorAll('.cb-pdp-hero__grid').forEach((grid) => {
      const galleryCol = grid.querySelector('[data-pdp-gallery-col]');
      const buyCol = grid.querySelector('[data-pdp-buy-col]');
      if (!galleryCol || !buyCol) return;

      const apply = () => {
        const desktop = mq.matches;
        grid.classList.toggle('cb-pdp-hero__grid--desktop', desktop);
        galleryCol.classList.toggle('is-pdp-gallery-sticky', desktop);
        galleryCol.style.minHeight = '';
      };

      apply();
      window.addEventListener('load', apply);
      mq.addEventListener('change', apply);
      if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(apply).observe(buyCol);
      }
      grid.querySelectorAll('.cb-pdp-accordion--cb, [data-pdp-box-accordion]').forEach((el) => {
        el.addEventListener('toggle', apply);
      });
    });
  })();

  // ---------- PDP "What's in the box" panel ----------
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-pdp-box-toggle]');
    if (!btn) return;
    const panelId = btn.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;
    if (!panel) return;
    const willOpen = panel.hidden;
    panel.hidden = !willOpen;
    btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    document.dispatchEvent(new CustomEvent('gwong:pdp:layout'));
  });
  document.addEventListener('gwong:pdp:layout', () => {
    window.dispatchEvent(new Event('resize'));
  });

  // ---------- Sticky ATC bar visibility (PDP) ----------
  const stickyAtc = $('[data-sticky-atc]') || $('.cb-pdp-sticky-atc');
  const mainAtc = $('[data-main-atc]') || $('[data-bogo-atc]');
  let stickyDismissed = false;

  if (stickyAtc && stickyAtc.parentElement !== document.body) {
    document.body.appendChild(stickyAtc);
  }

  if (stickyAtc) {
    const dismissBtn = $('[data-sticky-dismiss]', stickyAtc);
    dismissBtn?.addEventListener('click', () => {
      stickyDismissed = true;
      stickyAtc.classList.remove('is-visible');
      stickyAtc.setAttribute('aria-hidden', 'true');
    });
  }

  if (stickyAtc && mainAtc) {
    const atcAnchor = mainAtc.closest('.cb-pdp-actions') || mainAtc.closest('.cb-pdp-purchase') || mainAtc;

    const syncStickyAtc = () => {
      if (stickyDismissed) return;
      const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
      const chrome = window.Gwong.getSiteChromeHeight
        ? window.Gwong.getSiteChromeHeight()
        : { total: 0 };
      const revealOffset = chrome.total + 12;
      const show = atcAnchor.getBoundingClientRect().bottom < revealOffset;
      stickyAtc.classList.toggle('is-visible', show);
      stickyAtc.setAttribute('aria-hidden', show ? 'false' : 'true');
    };

    const onStickyAtcScroll = window.Gwong.utils.debounce(syncStickyAtc, 16);
    window.addEventListener('scroll', onStickyAtcScroll, { passive: true });
    window.addEventListener('resize', window.Gwong.utils.debounce(syncStickyAtc, 100));
    document.addEventListener('gwong:pdp:layout', syncStickyAtc);
    document.addEventListener('gwong:chrome', syncStickyAtc);

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(() => syncStickyAtc(), {
        root: null,
        threshold: [0, 0.01, 0.25, 0.5, 1],
      });
      io.observe(atcAnchor);
    }

    syncStickyAtc();
  }

  // ---------- Box accordion open on desktop ----------
  const boxAccordion = document.querySelector('[data-pdp-box-accordion][data-box-open-desktop]');
  if (boxAccordion) {
    const syncBoxAccordion = () => {
      if (window.innerWidth >= 1024) boxAccordion.setAttribute('open', '');
      else boxAccordion.removeAttribute('open');
    };
    syncBoxAccordion();
    window.addEventListener('resize', window.Gwong.utils.debounce(syncBoxAccordion, 150));
  }

  // ---------- PDP gallery zoom (slider in product-gallery.js) ----------
  $$('[data-gallery-main]').forEach((main) => {
    main.addEventListener('click', (e) => {
      if (e.target.closest('[data-gallery-prev], [data-gallery-next], [data-gallery-thumb]')) return;
      if (main.querySelector('.cb-pdp-gallery__viewport.is-dragging')) return;
      if (window.innerWidth < 768) return;
      const slide = main.querySelector('.cb-pdp-gallery__slide.is-active') || main.querySelector('[data-gallery-slide]');
      const img = slide && slide.querySelector('[data-gallery-slide-img]');
      if (!img) return;
      const isZoomed = main.classList.toggle('is-zoomed');
      if (isZoomed) {
        const rect = main.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = `${x}% ${y}%`;
      }
    });
    document.addEventListener('gwong:gallery:slide', () => {
      main.classList.remove('is-zoomed');
    });
  });

  // ---------- Variant selector (product page) ----------
  $$('[data-variant-picker]').forEach((picker) => {
    const radios = $$('input[type="radio"]', picker);
    const jsonEl = picker.querySelector('[data-variant-json]');
    const variantData = (() => {
      try { return JSON.parse((jsonEl && jsonEl.textContent) || picker.dataset.variants || '[]'); }
      catch { return []; }
    })();
    const applyVariant = (variant) => {
      if (!variant) return;
      const idInput = document.querySelector('[data-variant-id-input], input[name="id"]');
      if (idInput) idInput.value = variant.id;
      const onSale = variant.compare_at_price > variant.price;
      $$('[data-product-price]').forEach((el) => {
        el.textContent = variant.price_formatted;
        el.classList.toggle('is-sale', onSale);
      });
      const stickyPrice = document.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = variant.price_formatted;
      const installmentEl = document.querySelector('[data-installment-amount]');
      if (installmentEl && variant.price) {
        try {
          installmentEl.textContent = window.Gwong.utils.money(Math.round(variant.price / 4));
        } catch (_) {
          installmentEl.textContent = variant.price_formatted;
        }
      }
      $$('[data-product-compare]').forEach((el) => {
        if (onSale) {
          el.textContent = variant.compare_at_price_formatted;
          el.hidden = false;
        } else {
          el.hidden = true;
        }
      });
      const savingsBadge = document.querySelector('[data-product-savings-badge]');
      if (savingsBadge) {
        if (onSale && variant.compare_at_price) {
          const pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          savingsBadge.textContent = `Save ${pct}%`;
          savingsBadge.hidden = false;
        } else {
          savingsBadge.hidden = true;
        }
      }
      const blurb = picker.querySelector('[data-variant-blurb]');
      if (blurb && variant.blurb) blurb.textContent = variant.blurb;
      const stickyVariant = document.querySelector('[data-sticky-variant]');
      if (stickyVariant && variant.title) {
        const short = /pink/i.test(variant.title) ? 'Pink' : (/green/i.test(variant.title) ? 'Green' : variant.title);
        stickyVariant.textContent = short;
      }
      $$('.cb-pdp-swatch, .cb-pdp-variant-card', picker).forEach((card) => {
        const input = card.querySelector('input[type="radio"]');
        card.classList.toggle('is-selected', input && String(input.value) === String(variant.id));
      });
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
      document.dispatchEvent(new CustomEvent('gwong:variant:changed', { detail: { variant } }));
    };
    radios.forEach((radio) => {
      radio.addEventListener('change', () => {
        const variantId = radio.value;
        let variant = variantData.find((v) => String(v.id) === String(variantId));
        if (!variant) variant = variantData.find((v) => v.option_value === radio.value);
        applyVariant(variant);
      });
    });
  });

  // ---------- Bundle nudge opens bundle popup (Theme settings → Popups → Bundle) ----------
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-open-bundle]');
    if (!trigger || trigger.classList.contains('cb-pdp-bundle-nudge--disabled')) return;
    e.preventDefault();
    if (!window.gwongConfig?.popups?.bundleEnabled) {
      alert('Bundle offer is not enabled. Turn on “Bundle popup” in Theme settings → Popups.');
      return;
    }
    document.dispatchEvent(new CustomEvent('gwong:popup:bundle:open', { detail: { source: 'inline' } }));
  });

  // ---------- CurrentBody hero slider ----------
  const playHeroVideo = window.__gwongPlayHeroVideo || (window.__gwongPlayHeroVideo = (v) => {
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');
    v.playsInline = true;
    v.autoplay = true;
    const tryPlay = () => {
      if (v.readyState < 2 || !v.paused) return;
      const p = v.play();
      if (p?.catch) p.catch(() => {});
    };
    if (v.readyState >= 2) {
      tryPlay();
    } else {
      v.addEventListener('loadeddata', tryPlay, { once: true });
      v.addEventListener('canplay', tryPlay, { once: true });
    }
  });

  $$('[data-hero-slider]').forEach((hero) => {
    const slides = $$('[data-hero-slide]', hero);
    const prevBtn = $('[data-hero-prev]', hero);
    const nextBtn = $('[data-hero-next]', hero);
    const hydrateHeroVideo = (v) => {
      if (!v || v.dataset.hydrated) return;
      let changed = false;
      v.querySelectorAll('source[data-src]').forEach((s) => {
        s.src = s.dataset.src;
        s.removeAttribute('data-src');
        changed = true;
      });
      if (v.dataset.src) {
        v.src = v.dataset.src;
        v.removeAttribute('data-src');
        changed = true;
      }
      if (changed) v.load();
      v.dataset.hydrated = '1';
    };
    const syncHeroVideos = () => {
      const desktop = window.matchMedia('(min-width: 768px)').matches;
      $$('video', hero).forEach((v) => {
        const isShared = v.classList.contains('cb-hero__video--desktop') && v.classList.contains('cb-hero__video--mobile');
        const hide = !isShared && (
          (v.classList.contains('cb-hero__video--desktop') && !desktop) ||
          (v.classList.contains('cb-hero__video--mobile') && desktop)
        );
        if (hide) {
          v.pause();
          return;
        }
        hydrateHeroVideo(v);
        const slide = v.closest('[data-hero-slide]');
        if (slide && !slide.classList.contains('is-active')) {
          v.pause();
          return;
        }
        if (v.paused) playHeroVideo(v);
      });
    };
    syncHeroVideos();
    window.matchMedia('(min-width: 768px)').addEventListener('change', syncHeroVideos);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') syncHeroVideos();
    });
    const retryHeroOnGesture = () => {
      $$('video.cb-hero__video', hero).forEach((v) => {
        if (!v.paused) return;
        const slide = v.closest('[data-hero-slide]');
        if (slide && !slide.classList.contains('is-active')) return;
        playHeroVideo(v);
      });
    };
    ['touchstart', 'pointerdown', 'scroll'].forEach((evt) => {
      window.addEventListener(evt, retryHeroOnGesture, { once: true, passive: true });
    });
    if (slides.length < 2) {
      if (prevBtn) prevBtn.hidden = true;
      if (nextBtn) nextBtn.hidden = true;
      return;
    }
    let idx = slides.findIndex((s) => s.classList.contains('is-active'));
    if (idx < 0) idx = 0;
    const dotsEl = $('[data-hero-dots]', hero);
    const updateDots = () => {
      if (!dotsEl) return;
      $$('button', dotsEl).forEach((b, i) => b.classList.toggle('is-active', i === idx));
    };
    const updateHeroNav = () => {
      const single = slides.length <= 1;
      if (prevBtn) prevBtn.hidden = single || idx <= 0;
      if (nextBtn) nextBtn.hidden = single || idx >= slides.length - 1;
    };
    const go = (n) => {
      slides[idx].classList.remove('is-active');
      idx = Math.max(0, Math.min(n, slides.length - 1));
      slides[idx].classList.add('is-active');
      updateDots();
      updateHeroNav();
      syncHeroVideos();
    };
    if (dotsEl && !dotsEl.children.length) {
      slides.forEach((_, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('aria-label', `Slide ${i + 1}`);
        if (i === idx) b.classList.add('is-active');
        b.addEventListener('click', () => go(i));
        dotsEl.appendChild(b);
      });
    }
    prevBtn?.addEventListener('click', () => {
      if (idx > 0) go(idx - 1);
    });
    nextBtn?.addEventListener('click', () => {
      if (idx < slides.length - 1) go(idx + 1);
    });
    updateHeroNav();
    let timer = setInterval(() => {
      if (idx < slides.length - 1) go(idx + 1);
      else go(0);
    }, 6000);
    hero.addEventListener('mouseenter', () => clearInterval(timer));
    hero.addEventListener('mouseleave', () => {
      timer = setInterval(() => go(idx + 1), 6000);
    });
  });

  // ---------- Homepage carousels (bestsellers, reviews) ----------
  $$('[data-carousel-nav]').forEach((nav) => {
    const name = nav.dataset.carouselNav;
    const section = nav.closest('.cb-section');
    const carousel = section ? $(`[data-carousel="${name}"]`, section) : $(`[data-carousel="${name}"]`);
    if (!carousel) return;
    const prevBtn = $('[data-carousel-prev]', nav);
    const nextBtn = $('[data-carousel-next]', nav);
    const step = () => {
      const card = carousel.querySelector('.cb-product-card, .cb-review-card');
      return (card ? card.offsetWidth : 280) + 16;
    };
    const updateNav = () => window.Gwong.utils.updateScrollNav(carousel, prevBtn, nextBtn);
    prevBtn?.addEventListener('click', () => {
      carousel.scrollBy({ left: -step(), behavior: 'smooth' });
    });
    nextBtn?.addEventListener('click', () => {
      carousel.scrollBy({ left: step(), behavior: 'smooth' });
    });
    carousel.addEventListener(
      'scroll',
      () => requestAnimationFrame(updateNav),
      { passive: true }
    );
    window.addEventListener('resize', window.Gwong.utils.debounce(updateNav, 150));
    updateNav();
  });

  // ---------- Homepage sticky shop bar ----------
  const homeSticky = $('[data-home-sticky-bar]');
  if (homeSticky) {
    const toggleSticky = () => {
      const heroEl = $('#hero');
      const past = heroEl ? window.scrollY > heroEl.offsetHeight * 0.45 : window.scrollY > 420;
      homeSticky.classList.toggle('is-visible', past);
      homeSticky.setAttribute('aria-hidden', past ? 'false' : 'true');
    };
    window.addEventListener('scroll', toggleSticky, { passive: true });
    toggleSticky();
  }

  // ---------- Collection sort & filter ----------
  const collectionPage = $('[data-collection-page]');
  if (collectionPage) {
    const sortSelect = $('#CollectionSort', collectionPage);
    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sortSelect.value);
        window.location.assign(url.toString());
      });
    }

    const filterRoot = $('[data-collection-filter]', collectionPage);
    const filterToggle = $('[data-collection-filter-toggle]', collectionPage);
    const filterPanel = $('[data-collection-filter-panel]', collectionPage);
    if (filterRoot && filterToggle && filterPanel) {
      const closeFilter = () => {
        filterPanel.hidden = true;
        filterToggle.setAttribute('aria-expanded', 'false');
        filterRoot.classList.remove('is-open');
      };
      filterToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const open = filterPanel.hidden;
        if (open) {
          filterPanel.hidden = false;
          filterToggle.setAttribute('aria-expanded', 'true');
          filterRoot.classList.add('is-open');
        } else {
          closeFilter();
        }
      });
      document.addEventListener('click', (e) => {
        if (!filterRoot.contains(e.target)) closeFilter();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeFilter();
      });
    }
  }

  // ---------- Newsletter form (footer) ----------
  $$('[data-newsletter-form]').forEach((form) => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = form.querySelector('input[type="email"]').value;
      const status = form.querySelector('[data-newsletter-status]');
      try {
        if (window.Gwong?.submitEmail) {
          await window.Gwong.submitEmail(email, 'footer');
        }
        if (status) status.textContent = form.dataset.success || 'Welcome to the Glow Club. Check your inbox.';
        form.reset();
      } catch (err) {
        if (status) status.textContent = form.dataset.error || 'Something went wrong. Please try again.';
      }
    });
  });
})();
